import {
	differenceInCalendarDays,
	endOfWeek,
	format,
	getISOWeek,
	getMonth,
	getYear,
	isSameWeek,
	startOfMonth,
	startOfWeek,
} from "date-fns";
import { cs } from "date-fns/locale/cs";
import { DEFAULT_DATE_FORMAT } from "web_myplann/Shared/Utils/dateFormatters";

export const WEEK_OPTIONS = { weekStartsOn: 1 } as const;
export const EARLIER_WEEK_HORIZON_DAYS = 30;

export type RecentBucket =
	| { kind: "today"; key: "today" }
	| { kind: "thisWeek"; key: "thisWeek" }
	| { kind: "earlierWeek"; key: string; representativeDate: Date }
	| { kind: "earlierMonth"; key: string; representativeDate: Date };

const capitalizeFirst = (value: string): string => {
	if (value.length === 0) return value;
	return value.charAt(0).toUpperCase() + value.slice(1);
};

export const formatWeekLabel = (date: Date): string => {
	const start = startOfWeek(date, WEEK_OPTIONS);
	const end = endOfWeek(date, WEEK_OPTIONS);
	return `${format(start, "dd.MM.", { locale: cs })} – ${format(
		end,
		DEFAULT_DATE_FORMAT,
		{ locale: cs },
	)}`;
};

export const formatMonthLabel = (date: Date): string =>
	capitalizeFirst(format(date, "LLLL yyyy", { locale: cs }));

const earlierWeekKey = (date: Date): string =>
	`earlier-week-${getYear(date)}-W${String(getISOWeek(date)).padStart(2, "0")}`;

const earlierMonthKey = (date: Date): string =>
	`earlier-month-${getYear(date)}-${String(getMonth(date) + 1).padStart(2, "0")}`;

export const classifyRecentDate = (
	itemDate: Date,
	today: Date,
	cutoffEarlierWeek: Date,
): RecentBucket => {
	if (differenceInCalendarDays(today, itemDate) === 0) {
		return { kind: "today", key: "today" };
	}
	if (isSameWeek(itemDate, today, WEEK_OPTIONS)) {
		return { kind: "thisWeek", key: "thisWeek" };
	}
	const itemWeekStart = startOfWeek(itemDate, WEEK_OPTIONS);
	if (
		differenceInCalendarDays(itemWeekStart, cutoffEarlierWeek) >= 0 &&
		differenceInCalendarDays(itemDate, today) < 0
	) {
		return {
			kind: "earlierWeek",
			key: earlierWeekKey(itemDate),
			representativeDate: itemWeekStart,
		};
	}
	return {
		kind: "earlierMonth",
		key: earlierMonthKey(itemDate),
		representativeDate: startOfMonth(itemDate),
	};
};
